import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Select, 
  DatePicker, 
  Tag, 
  Space, 
  message,
  Typography,
  Row,
  Col,
  Statistic
} from 'antd';
import { 
  UserAddOutlined, 
  LogoutOutlined, 
  ReloadOutlined,
  ClockCircleOutlined 
} from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;

export default function CheckinsPage() {
  const [checkins, setCheckins] = useState([]);
  const [matriculas, setMatriculas] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalEntrada, setModalEntrada] = useState(false);
  const [modalSaida, setModalSaida] = useState(false);
  const [checkinSelecionado, setCheckinSelecionado] = useState(null);
  const [dataFiltro, setDataFiltro] = useState(dayjs());

  const [formEntrada] = Form.useForm();
  const [formSaida] = Form.useForm();

  // Carregar dados iniciais
  useEffect(() => {
    carregarGrupos();
    carregarMatriculas();
    carregarResponsaveis();
  }, []);

  // Carregar check-ins quando mudar a data
  useEffect(() => {
    carregarCheckins();
  }, [dataFiltro]);

  const carregarGrupos = async () => {
    try {
      const res = await api.get('/creche/grupos');
      setGrupos(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const carregarMatriculas = async () => {
    try {
      const res = await api.get('/creche/matriculas');
      // Filtrar apenas matrículas ativas
      const ativas = (res.data || []).filter(m => 
        m.status === 'ATIVA' || m.status === 'AGUARDANDO_PAGAMENTO'
      );
      setMatriculas(ativas);
    } catch (error) {
      console.error('Erro ao carregar matrículas:', error);
    }
  };

  const carregarResponsaveis = async () => {
    try {
      const res = await api.get('/creche/responsaveis');
      setResponsaveis(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar responsáveis:', error);
    }
  };

  const carregarCheckins = async () => {
    try {
      setLoading(true);
      const data = dataFiltro.format('YYYY-MM-DD');
      const res = await api.get('/creche/checkins', { params: { data } });
      setCheckins(res.data || []);
    } catch (error) {
      message.error('Erro ao carregar check-ins');
    } finally {
      setLoading(false);
    }
  };

  const abrirModalEntrada = () => {
    formEntrada.resetFields();
    setModalEntrada(true);
  };

  const abrirModalSaida = (checkin) => {
    setCheckinSelecionado(checkin);
    formSaida.resetFields();
    setModalSaida(true);
  };

  const registrarEntrada = async (values) => {
    try {
      setLoading(true);
      const matricula = matriculas.find(m => m.id === values.matriculaId);
      
      await api.post('/creche/checkins', {
        estudanteId: matricula.estudanteId,
        grupoId: matricula.grupoId,
        entreguePorRespId: values.entreguePorRespId,
        metodo: values.metodo || 'MANUAL',
        observacoes: values.observacoes
      });

      message.success('Entrada registrada com sucesso!');
      setModalEntrada(false);
      carregarCheckins();
    } catch (error) {
      const msg = error.response?.data?.erro || 'Erro ao registrar entrada';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const registrarSaida = async (values) => {
    try {
      setLoading(true);
      
      await api.patch(`/creche/checkins/${checkinSelecionado.id}/saida`, {
        levantadoPorRespId: values.levantadoPorRespId,
        observacoes: values.observacoes
      });

      message.success('Saída registrada com sucesso!');
      setModalSaida(false);
      carregarCheckins();
    } catch (error) {
      const msg = error.response?.data?.erro || 'Erro ao registrar saída';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Estatísticas
  const presentes = checkins.filter(c => c.status === 'PRESENTE').length;
  const sairam = checkins.filter(c => c.status === 'SAIU').length;

  // Colunas da tabela
  const columns = [
    {
      title: 'Criança',
      dataIndex: 'estudanteNome',
      key: 'estudanteNome',
      sorter: (a, b) => a.estudanteNome.localeCompare(b.estudanteNome),
    },
    {
      title: 'Grupo',
      dataIndex: 'grupoNome',
      key: 'grupoNome',
      filters: grupos.map(g => ({ text: g.nome, value: g.nome })),
      onFilter: (value, record) => record.grupoNome === value,
    },
    {
      title: 'Entrada',
      dataIndex: 'dataEntrada',
      key: 'dataEntrada',
      render: (data) => dayjs(data).format('HH:mm'),
      sorter: (a, b) => dayjs(a.dataEntrada).unix() - dayjs(b.dataEntrada).unix(),
    },
    {
      title: 'Entregue Por',
      dataIndex: 'entreguePor',
      key: 'entreguePor',
      render: (nome) => nome || '—',
    },
    {
      title: 'Saída',
      dataIndex: 'dataSaida',
      key: 'dataSaida',
      render: (data) => data ? dayjs(data).format('HH:mm') : '—',
    },
    {
      title: 'Levantado Por',
      dataIndex: 'levantadoPor',
      key: 'levantadoPor',
      render: (nome) => nome || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'PRESENTE' ? 'green' : 'blue'}>
          {status}
        </Tag>
      ),
      filters: [
        { text: 'Presente', value: 'PRESENTE' },
        { text: 'Saiu', value: 'SAIU' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => (
        record.status === 'PRESENTE' ? (
          <Button 
            size="small" 
            icon={<LogoutOutlined />}
            onClick={() => abrirModalSaida(record)}
          >
            Registrar Saída
          </Button>
        ) : null
      ),
    },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <Title level={2}>Check-ins e Saídas</Title>
      </div>

      {/* Estatísticas */}
      <Row gutter={16} className="mb-6">
        <Col span={8}>
          <Card>
            <Statistic 
              title="Presentes Agora" 
              value={presentes} 
              valueStyle={{ color: '#3f8600' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic 
              title="Já Saíram" 
              value={sairam} 
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic 
              title="Total do Dia" 
              value={checkins.length} 
            />
          </Card>
        </Col>
      </Row>

      {/* Filtros e Ações */}
      <Card className="mb-4">
        <Space>
          <DatePicker 
            value={dataFiltro}
            onChange={(date) => setDataFiltro(date || dayjs())}
            format="DD/MM/YYYY"
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={carregarCheckins}
            loading={loading}
          >
            Atualizar
          </Button>
          <Button 
            type="primary" 
            icon={<UserAddOutlined />}
            onClick={abrirModalEntrada}
          >
            Registrar Entrada
          </Button>
        </Space>
      </Card>

      {/* Tabela de Check-ins */}
      <Card>
        <Table 
          columns={columns}
          dataSource={checkins}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          locale={{ 
            emptyText: 'Nenhum check-in registrado para esta data' 
          }}
        />
      </Card>

      {/* Modal: Registrar Entrada */}
      <Modal
        title="Registrar Entrada"
        open={modalEntrada}
        onCancel={() => setModalEntrada(false)}
        onOk={() => formEntrada.submit()}
        confirmLoading={loading}
      >
        <Form form={formEntrada} layout="vertical" onFinish={registrarEntrada}>
          <Form.Item 
            name="matriculaId" 
            label="Criança" 
            rules={[{ required: true, message: 'Selecione uma criança' }]}
          >
            <Select
              showSearch
              placeholder="Selecione a criança"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={matriculas.map(m => ({
                value: m.id,
                label: `${m.estudanteNome} - ${m.grupoNome}`
              }))}
            />
          </Form.Item>

          <Form.Item name="entreguePorRespId" label="Entregue Por (Opcional)">
            <Select
              showSearch
              placeholder="Selecione o responsável"
              allowClear
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={responsaveis.map(r => ({
                value: r.id,
                label: r.nome
              }))}
            />
          </Form.Item>

          <Form.Item name="metodo" label="Método" initialValue="MANUAL">
            <Select>
              <Select.Option value="MANUAL">Manual</Select.Option>
              <Select.Option value="QRCODE">QR Code</Select.Option>
              <Select.Option value="PIN">PIN</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal: Registrar Saída */}
      <Modal
        title={`Registrar Saída - ${checkinSelecionado?.estudanteNome}`}
        open={modalSaida}
        onCancel={() => setModalSaida(false)}
        onOk={() => formSaida.submit()}
        confirmLoading={loading}
      >
        <Form form={formSaida} layout="vertical" onFinish={registrarSaida}>
          <Form.Item 
            name="levantadoPorRespId" 
            label="Levantado Por"
            rules={[{ required: true, message: 'Selecione quem está levantando a criança' }]}
          >
            <Select
              showSearch
              placeholder="Selecione o responsável"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={responsaveis.map(r => ({
                value: r.id,
                label: r.nome
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}